import { useId } from "react";
import { Switch } from "../ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { ANALYTICS_INPUT_COPY } from "../../lib/analyticsFeeds";
import { isAnalyticsInputFileExtension } from "../../lib/fileTypes";

/**
 * The affordance that sets and clears the per-document analytics-input flag.
 *
 * It presupposes no binding: the flag is stored rather than derived precisely so
 * the auditor can set it before any parameter has been chosen, and a control
 * that needed a parameter first would put those two in the wrong order.
 *
 * Nothing here writes. The caller owns the request, so the same control can sit
 * on any surface that renders a session document without each of them growing
 * its own copy of the write.
 */
interface AnalyticsInputToggleProps {
  flagged: boolean;
  /**
   * The file this control answers for, carried into the accessible name. The
   * column heading says what the control means, but one heading cannot say
   * which row it belongs to.
   */
  documentName: string;
  /**
   * The file's format, which decides whether marking it is offered at all. A
   * workflow run reads its input as rows and can only open the formats the
   * engine ships a reader for, so a format failing either test cannot answer a
   * parameter and the write route refuses it.
   *
   * Optional, and an absent format reads as not flaggable: a caller that cannot
   * say what the file is has not established that a run could consume it.
   */
  documentFileType?: string | null;
  /**
   * Whether an answer was read at all — the record behind `flagged` found, and
   * the flag on it present. False says `flagged` carries no answer, and the
   * control states that rather than the `No` an absence spells.
   *
   * Required, with no default: holding the flag's own row is not enough to know
   * it, because the record can arrive without the field, and every default here
   * would be the certainty this prop exists to withhold.
   */
  flagKnown: boolean;
  onChange: (next: boolean) => void;
  pending?: boolean;
  /**
   * Whether any binding write is in flight, this row's or another row's.
   *
   * Bars unmarking alone. The unmark clears this document's assignments from a
   * snapshot taken at its start, so one opened over another write would decide
   * what to clear from a list that write is still changing. Marking writes only
   * the flag and races nothing, so it stays live throughout.
   */
  bindingWriteOpen?: boolean;
  /**
   * Why the switch is held whichever way it points, or null. Shown in a
   * tooltip, the way Send to Optro shows its sign-in reason.
   */
  heldReason?: string | null;
}

export function AnalyticsInputToggle({
  flagged,
  documentName,
  documentFileType,
  flagKnown,
  onChange,
  pending = false,
  bindingWriteOpen = false,
  heldReason = null,
}: AnalyticsInputToggleProps) {
  // The write route is the guarantee, and it reads the format it stored rather
  // than anything sent from here; this only avoids offering an action that
  // would be refused.
  //
  // The gate is on marking alone. A file flagged before the rule existed may
  // hold any format — the pre-fill writes the same flag from a file's category,
  // which knows nothing about rows — and disabling the control for those would
  // strand them marked with no way to take it back.
  const marksAreOffered = isAnalyticsInputFileExtension(documentFileType);
  const withholdsMark = !marksAreOffered && !flagged;
  // Unmarking clears the document's assignments before it writes the flag, so
  // it may not open over a binding write already changing the list those clears
  // are named from. Marking writes only the flag and races nothing, so this
  // gate is on unmarking alone.
  const withholdsUnmark = flagged && bindingWriteOpen;
  // A format nobody has told us yet is not a format we can refuse. Withhold the
  // control either way, but only state the format rule when we actually read
  // one: a caller passing no format is the ordinary case while the document
  // record is still resolving, and the file may well be one the rule allows.
  // A record we have not read cannot be refused for its format either, so the
  // unknown state is the only reason shown: two of them at once would have the
  // control both undecided and ruled out.
  const explainsRefusal =
    flagKnown && withholdsMark && Boolean(documentFileType);
  // The `switch` role speaks the position, so `Yes` and `No` in the name would
  // be the state read twice. The third answer has no position of its own: an
  // unread flag draws the same unchecked, dimmed switch a `No` does, and the
  // name is the only place the two can differ.
  const subject = `${ANALYTICS_INPUT_COPY.flagLabel} — ${documentName}`;
  const accessibleName = flagKnown
    ? subject
    : `${subject} — ${ANALYTICS_INPUT_COPY.flagUnknown}`;
  const noteId = useId();
  const unknownNoteId = `${noteId}-unknown`;
  const refusalNoteId = `${noteId}-refusal`;
  // A refusal that stands until something else changes gets a sentence beside
  // the switch; one that clears itself gets none. A disabled control is not in
  // the tab order, so a standing reason has nowhere but the page to be found —
  // a hover sits on a control nobody can reach. A reason that clears itself
  // wants no sentence at all: every flagged row watches the same in-flight
  // write, so a note for one would appear across all of them and be gone
  // before it was read. At most one sentence holds, for the reason
  // `explainsRefusal` states.
  const describedBy = !flagKnown
    ? unknownNoteId
    : explainsRefusal
      ? refusalNoteId
      : undefined;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            {/* span: a disabled switch takes no focus or pointer events, so
                the tooltip needs an enabled wrapper a keyboard can reach. */}
            <span
              tabIndex={heldReason !== null ? 0 : undefined}
              className="inline-flex"
            >
              <Switch
                checked={flagged}
                disabled={
                  pending ||
                  withholdsMark ||
                  withholdsUnmark ||
                  !flagKnown ||
                  heldReason !== null
                }
                aria-label={accessibleName}
                aria-describedby={describedBy}
                onCheckedChange={onChange}
              />
            </span>
          </TooltipTrigger>
          {heldReason !== null && <TooltipContent>{heldReason}</TooltipContent>}
        </Tooltip>
        {/* The state in words beside the switch, because a two-position control
            with nothing but a thumb tells a reader which way it is pointing and
            not which way means yes. Not a `label`: the switch already carries
            its own accessible name, and one naming it again would replace the
            name that says which row this is. */}
        <span
          aria-hidden="true"
          className="text-sm text-font-secondary"
          title={flagKnown ? undefined : ANALYTICS_INPUT_COPY.flagUnknownTitle}
        >
          {!flagKnown
            ? ANALYTICS_INPUT_COPY.flagUnknown
            : flagged
              ? ANALYTICS_INPUT_COPY.flagOn
              : ANALYTICS_INPUT_COPY.flagOff}
        </span>
        {/* The long form of the unknown state, spoken. The `title` carrying it
            beside this is a hover, which a keyboard reaches on nothing and a
            switch nobody can focus reaches least of all. */}
        {!flagKnown && (
          <span id={unknownNoteId} className="sr-only">
            {ANALYTICS_INPUT_COPY.flagUnknownTitle}
          </span>
        )}
      </div>
      {explainsRefusal && (
        <span id={refusalNoteId} className="text-xs text-font-secondary">
          {ANALYTICS_INPUT_COPY.notFlaggable}
        </span>
      )}
    </div>
  );
}
